import { JSX, useCallback, useEffect, useState } from "react";

import { IconButton, TableCell } from "@mui/material";
import TableRow from "@mui/material/TableRow";

import DeleteIcon from "@mui/icons-material/Delete";

import IndexCheckbox from "utils/elements/IndexCheckbox";
import { AddEvent, ChangeEvent, Event, EventGroup } from "utils/history/Event";

import { InformationRight } from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { calculateRightStatus } from "core/trajectory/access/AccessUtil";
import { RoleRowProps } from "core/trajectory/access/RoleTable";

export type InformationRoleRowProps = RoleRowProps & {
	selected: string[];
};

const RIGHTS_SIZE = 3;
const DEFAULT_RIGHTS = Array(RIGHTS_SIZE).fill(false);

function InformationRoleRow(props: InformationRoleRowProps): JSX.Element {
	const [rightsOverview, setRightsOverview] = useState(DEFAULT_RIGHTS);

	useEffect(() => {
		const rights = props.role.informationRights;
		const selected = props.selected;
		const status = calculateRightStatus(rights, selected, RIGHTS_SIZE);
		setRightsOverview(status);
	}, [props.selected, props.role.informationRights]);

	const toggleRight = useCallback(
		(right: InformationRight, enabled: boolean): void => {
			const basePath = `/roles/${props.index}/informationRights/`;

			const events: Event[] = [];
			for (const key of props.selected) {
				const currentRights = props.role.informationRights[key];

				const keyPath = basePath + key;
				if (currentRights === undefined) {
					events.push(new AddEvent(keyPath, DEFAULT_RIGHTS));
				}

				const rights = getImpliedRights(right, enabled);
				for (const [impliedRight, status] of rights.entries()) {
					const path = `${keyPath}/${impliedRight}`;
					const hasRight = currentRights?.[impliedRight] ?? false;

					if (status !== hasRight) {
						events.push(new ChangeEvent(path, !status, status));
					}
				}
			}

			const group = new EventGroup(events);
			props.onChange(group);
		},
		[
			props.index,
			props.role.informationRights,
			props.selected,
			props.onChange
		]
	);

	function getImpliedRights(
		right: InformationRight,
		enabled: boolean
	): Map<InformationRight, boolean> {
		const rights = new Map<InformationRight, boolean>();
		rights.set(right, enabled);

		if (enabled) {
			if (right === InformationRight.WRITE) {
				rights.set(InformationRight.READ, true);
				rights.set(InformationRight.HIDDEN, false);
			}
			if (right === InformationRight.READ) {
				rights.set(InformationRight.HIDDEN, false);
			}
			if (right === InformationRight.HIDDEN) {
				rights.set(InformationRight.WRITE, false);
				rights.set(InformationRight.READ, false);
			}
		} else {
			if (right === InformationRight.READ) {
				rights.set(InformationRight.WRITE, false);
			}
		}

		return rights;
	}

	const onDelete = useCallback((): void => {
		props.onDelete(props.index);
	}, [props.index, props.onDelete]);

	function isDisabled(): boolean {
		return !props.editable || props.selected.length === 0;
	}

	return (
		<TableRow>
			<TableCell component="th" scope="row">
				{props.role.name}
			</TableCell>
			<TableCell padding="checkbox" align="center">
				<IndexCheckbox
					index={InformationRight.WRITE}
					disabled={isDisabled()}
					status={rightsOverview[InformationRight.WRITE]}
					onChange={toggleRight}
				/>
			</TableCell>
			<TableCell padding="checkbox" align="center">
				<IndexCheckbox
					index={InformationRight.READ}
					disabled={isDisabled()}
					status={rightsOverview[InformationRight.READ]}
					onChange={toggleRight}
				/>
			</TableCell>
			<TableCell padding="checkbox" align="center">
				<IndexCheckbox
					index={InformationRight.HIDDEN}
					disabled={isDisabled()}
					status={rightsOverview[InformationRight.HIDDEN]}
					onChange={toggleRight}
				/>
			</TableCell>
			<TableCell padding="none" align="right">
				<IconButton
					size="small"
					disabled={!props.editable}
					onClick={onDelete}>
					<DeleteIcon fontSize="inherit"></DeleteIcon>
				</IconButton>
			</TableCell>
		</TableRow>
	);
}

export default InformationRoleRow;
